import { createStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import {
  type PortableSharepointPickerBreadcrumb,
  getSelectionKey,
  isSharepointItemAccepted,
  sharepointDriveItemToPickerItem,
} from '@/components/integrations/SharepointFilePicker/model'
import type {
  SharepointPickerItem,
  SharepointSelectionPolicy,
} from '@/components/integrations/SharepointFilePicker/types'

import type {
  SharePointDrive,
  SharePointDriveItem,
  SharePointSite,
} from '@/hooks/sharepoint/types'

import type { UUID } from '@/types/Id'

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Everything that decides whether an item may be selected. */
export interface SelectionRules {
  /** Extensions and MIME types the caller will take; empty means anything. */
  accept: readonly string[]
  multiple: boolean
  selectionPolicy: SharepointSelectionPolicy
}

const isSelectable = (item: SharePointDriveItem, rules: SelectionRules) => {
  if (item.is_folder) {
    return rules.selectionPolicy === 'files_and_folders'
  }

  return isSharepointItemAccepted(item, rules.accept)
}

const createSelectionRecord = (
  items: readonly SharepointPickerItem[],
): Record<string, SharepointPickerItem> => {
  const record: Record<string, SharepointPickerItem> = {}
  for (const item of items) {
    record[getSelectionKey(item)] = item
  }
  return record
}

// ─── State shape ─────────────────────────────────────────────────────────────

export interface SharepointPickerStoreState {
  // Navigation
  currentSite: SharePointSite | null
  currentSitePath: SharePointSite[]
  currentDrive: SharePointDrive | null
  currentFolderId: string | null
  breadcrumbs: PortableSharepointPickerBreadcrumb[]

  // Selection
  selectedItems: Record<string, SharepointPickerItem>
  selectedCount: number
  selectedTagIds: UUID[]
  lastClickedIndex: number | null

  // UI
  nativeFallbackOpen: boolean
  isSubmitting: boolean

  // ── Actions ──

  // Navigation
  selectDriveInSite: (site: SharePointSite, drive: SharePointDrive) => void
  clearCurrentDriveSelection: () => void
  setCurrentSitePath: (sitePath: SharePointSite[]) => void
  navigateToFolder: (folder: SharePointDriveItem) => void
  navigateToBreadcrumb: (index: number) => void

  // Selection
  toggleSelection: (
    item: SharePointDriveItem,
    driveId: string,
    index: number,
    shiftKey: boolean,
    allItems: SharePointDriveItem[],
    rules: SelectionRules,
  ) => void
  removeSelectedItem: (key: string) => void
  clearSelection: () => void
  setTagIds: (ids: UUID[]) => void

  // UI
  setNativeFallbackOpen: (open: boolean) => void
  setIsSubmitting: (submitting: boolean) => void

  // Lifecycle — called on dialog open transition
  reset: (initialItems: SharepointPickerItem[], initialTagIds: UUID[]) => void
}

// ─── Store factory ───────────────────────────────────────────────────────────

export const createSharepointPickerStore = (
  initialItems: SharepointPickerItem[] = [],
  initialTagIds: UUID[] = [],
) => {
  const initialSelection = createSelectionRecord(initialItems)

  return createStore<SharepointPickerStoreState>()(
    immer((set) => ({
      // Initial state
      currentSite: null,
      currentSitePath: [],
      currentDrive: null,
      currentFolderId: null,
      breadcrumbs: [],
      selectedItems: initialSelection,
      selectedCount: Object.keys(initialSelection).length,
      selectedTagIds: [...initialTagIds],
      lastClickedIndex: null,
      nativeFallbackOpen: false,
      isSubmitting: false,

      // ── Navigation ──

      selectDriveInSite: (site, drive) =>
        set((state) => {
          const preservedSitePath =
            state.currentSitePath[state.currentSitePath.length - 1]?.id ===
            site.id
              ? state.currentSitePath
              : [site]

          state.currentSite = site
          state.currentSitePath = [...preservedSitePath]
          state.currentDrive = drive
          state.currentFolderId = null
          state.breadcrumbs = [
            { id: drive.id, name: drive.name, type: 'drive' },
          ]
          state.lastClickedIndex = null
        }),

      clearCurrentDriveSelection: () =>
        set((state) => {
          state.currentDrive = null
          state.currentFolderId = null
          state.breadcrumbs = []
          state.lastClickedIndex = null
        }),

      setCurrentSitePath: (sitePath) =>
        set((state) => {
          state.currentSitePath = [...sitePath]
        }),

      navigateToFolder: (folder) =>
        set((state) => {
          state.currentFolderId = folder.id
          state.lastClickedIndex = null
          const existingIndex = state.breadcrumbs.findIndex(
            (b) => b.id === folder.id,
          )
          if (existingIndex !== -1) {
            state.breadcrumbs = state.breadcrumbs.slice(0, existingIndex + 1)
          } else {
            state.breadcrumbs.push({
              id: folder.id,
              name: folder.name,
              type: 'folder',
            })
          }
        }),

      navigateToBreadcrumb: (index) =>
        set((state) => {
          const breadcrumb = state.breadcrumbs[index]
          if (!breadcrumb) return
          state.lastClickedIndex = null
          if (breadcrumb.type === 'drive') {
            state.currentFolderId = null
            state.breadcrumbs = state.breadcrumbs.slice(0, 1)
          } else {
            state.currentFolderId = breadcrumb.id
            state.breadcrumbs = state.breadcrumbs.slice(0, index + 1)
          }
        }),

      // ── Selection ──

      toggleSelection: (item, driveId, index, shiftKey, allItems, rules) =>
        set((state) => {
          if (!isSelectable(item, rules)) return

          if (shiftKey && rules.multiple && state.lastClickedIndex != null) {
            const start = Math.min(state.lastClickedIndex, index)
            const end = Math.max(state.lastClickedIndex, index)
            for (let i = start; i <= end; i++) {
              const rangeItem = allItems[i]
              if (!rangeItem) continue
              // A range sweeps over whatever sits between its ends, so what the
              // rows grey out has to be skipped here too.
              if (!isSelectable(rangeItem, rules)) continue
              const pickerItem = sharepointDriveItemToPickerItem(
                rangeItem,
                driveId,
              )
              state.selectedItems[getSelectionKey(pickerItem)] = pickerItem
            }
            state.selectedCount = Object.keys(state.selectedItems).length
            state.lastClickedIndex = index
            return
          }

          state.lastClickedIndex = index
          const pickerItem = sharepointDriveItemToPickerItem(item, driveId)
          const key = getSelectionKey(pickerItem)
          if (state.selectedItems[key]) {
            delete state.selectedItems[key]
          } else {
            if (!rules.multiple) {
              state.selectedItems = {}
            }
            state.selectedItems[key] = pickerItem
          }
          state.selectedCount = Object.keys(state.selectedItems).length
        }),

      removeSelectedItem: (key) =>
        set((state) => {
          delete state.selectedItems[key]
          state.selectedCount = Object.keys(state.selectedItems).length
        }),

      clearSelection: () =>
        set((state) => {
          state.selectedItems = {}
          state.selectedCount = 0
        }),

      setTagIds: (ids) =>
        set((state) => {
          state.selectedTagIds = ids
        }),

      // ── UI ──

      setNativeFallbackOpen: (open) =>
        set((state) => {
          state.nativeFallbackOpen = open
        }),

      setIsSubmitting: (submitting) =>
        set((state) => {
          state.isSubmitting = submitting
        }),

      // ── Lifecycle ──

      reset: (resetItems, resetTagIds) =>
        set((state) => {
          state.currentSite = null
          state.currentSitePath = []
          state.currentDrive = null
          state.currentFolderId = null
          state.breadcrumbs = []
          const record = createSelectionRecord(resetItems)
          state.selectedItems = record
          state.selectedCount = Object.keys(record).length
          state.selectedTagIds = [...resetTagIds]
          state.nativeFallbackOpen = false
          state.isSubmitting = false
          state.lastClickedIndex = null
        }),
    })),
  )
}

export type SharepointPickerStore = ReturnType<
  typeof createSharepointPickerStore
>
